import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { UserFriendlyError } from '../../../model/Error'
import { useCallback, useMemo } from 'react'
import { Filters } from '../../../model/Filters'
import { singleItemPageLimit, startingPage } from '../../../constants'
import { getNextPageParam } from '../../../misc/query.utilities'
import { usePaginationState, useFlatPaginatedData } from '../../utils/query'
import { medicalEventsKeys } from './medicalEvents.keys'
import { getMedicalEvents } from '../../../data/medicalEvents'

interface GetInfiniteMedicalEventsParams {
  enabled?: boolean
  filters?: Filters
  limit?: number
}

export const useGetInfiniteMedicalEvents = ({
  enabled = true,
  filters,
  limit = singleItemPageLimit,
}: GetInfiniteMedicalEventsParams = {}) => {
  const queryKey = useMemo(() => medicalEventsKeys.list(filters), [filters])

  const queryReturn = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data, ok } = await getMedicalEvents(pageParam, filters, signal, limit)
      if (!data || !ok) {
        throw new UserFriendlyError(
          'error',
          `Impossible de récupérer la liste des évènements médicaux`,
          `Une erreur technique s'est produite`,
        )
      }
      return data
    },
    enabled,
    initialPageParam: startingPage,
    getNextPageParam,
  })

  const queryClient = useQueryClient()
  const medicalEventList = useFlatPaginatedData(queryReturn.data)
  const paginationState = usePaginationState(queryReturn.data)

  const cancelPendingQuery = useCallback(() => {
    queryClient.cancelQueries({ queryKey })
  }, [queryClient, queryKey])

  return { query: queryReturn, medicalEventList, paginationState, cancelPendingQuery }
}
